import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';

interface DeliveryNote {
  id: string;
  projectId: string;
  number: string;
  supplier: string;
  items: { description: string; quantity: number; unit: string }[];
  deliveryDate: string;
  receivedBy: string | null;
  notes: string | null;
  project?: { id: string; title: string };
}

export default function DeliveryNotesPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({
    projectId: '', number: '', supplier: '', deliveryDate: new Date().toISOString().slice(0, 10),
    receivedBy: '', notes: '',
    items: [{ description: '', quantity: 1, unit: 'Stk' }] as { description: string; quantity: number; unit: string }[],
  });

  const { data: notes = [], isLoading } = useQuery<DeliveryNote[]>({
    queryKey: ['delivery-notes'],
    queryFn: () => api.get('/delivery-notes').then(r => r.data),
  });

  const { data: projects = [] } = useQuery<{ id: string; title: string }[]>({
    queryKey: ['projects-list'],
    queryFn: () => api.get('/projects').then(r => r.data),
  });

  const save = useMutation({
    mutationFn: (data: any) =>
      editId ? api.put(`/delivery-notes/${editId}`, data) : api.post('/delivery-notes', data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['delivery-notes'] }); closeForm(); },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/delivery-notes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['delivery-notes'] }),
  });

  function openNew() {
    setForm({
      projectId: '', number: '', supplier: '',
      deliveryDate: new Date().toISOString().slice(0, 10),
      receivedBy: '', notes: '',
      items: [{ description: '', quantity: 1, unit: 'Stk' }],
    });
    setEditId(null);
    setShowForm(true);
  }

  function openEdit(n: DeliveryNote) {
    setForm({
      projectId: n.projectId,
      number: n.number,
      supplier: n.supplier,
      deliveryDate: n.deliveryDate.slice(0, 10),
      receivedBy: n.receivedBy || '',
      notes: n.notes || '',
      items: n.items.length > 0 ? n.items : [{ description: '', quantity: 1, unit: 'Stk' }],
    });
    setEditId(n.id);
    setShowForm(true);
  }

  function closeForm() { setShowForm(false); setEditId(null); }

  function addItem() {
    setForm({ ...form, items: [...form.items, { description: '', quantity: 1, unit: 'Stk' }] });
  }

  function updateItem(idx: number, field: string, value: any) {
    const items = [...form.items];
    (items[idx] as any)[field] = value;
    setForm({ ...form, items });
  }

  function removeItem(idx: number) {
    if (form.items.length <= 1) return;
    setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Lieferscheine</h1>
          <p className="text-sm text-slate-500 mt-1">Materiallieferungen dokumentieren und Projekten zuordnen</p>
        </div>
        <button onClick={openNew} className="sp-btn-primary">+ Lieferschein</button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="sp-card p-6 mb-6 animate-scale-in">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">
            {editId ? 'Lieferschein bearbeiten' : 'Neuer Lieferschein'}
          </h3>
          <form onSubmit={(e) => { e.preventDefault(); save.mutate(form); }}>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <label className="sp-label">Nummer</label>
                <input type="text" className="sp-input" placeholder="LS-2026-001" value={form.number} onChange={e => setForm({ ...form, number: e.target.value })} required />
              </div>
              <div>
                <label className="sp-label">Projekt</label>
                <select className="sp-input" value={form.projectId} onChange={e => setForm({ ...form, projectId: e.target.value })} required>
                  <option value="">Bitte wählen...</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
              </div>
              <div>
                <label className="sp-label">Lieferant</label>
                <input type="text" className="sp-input" placeholder="z.B. Firma Müller" value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} />
              </div>
              <div>
                <label className="sp-label">Lieferdatum</label>
                <input type="date" className="sp-input" value={form.deliveryDate} onChange={e => setForm({ ...form, deliveryDate: e.target.value })} />
              </div>
              <div>
                <label className="sp-label">Angenommen von</label>
                <input type="text" className="sp-input" placeholder="Name" value={form.receivedBy} onChange={e => setForm({ ...form, receivedBy: e.target.value })} />
              </div>
              <div>
                <label className="sp-label">Notiz</label>
                <input type="text" className="sp-input" placeholder="Optional..." value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>

            {/* Items */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="sp-label !mb-0">Positionen</label>
                <button type="button" onClick={addItem} className="text-sm text-brand-500 hover:text-brand-600 font-medium">+ Position</button>
              </div>
              <div className="space-y-2">
                {form.items.map((item, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <input type="text" className="sp-input flex-[3]" placeholder="Beschreibung" value={item.description}
                      onChange={e => updateItem(idx, 'description', e.target.value)} />
                    <input type="number" className="sp-input flex-[1]" placeholder="Menge" value={item.quantity}
                      onChange={e => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)} />
                    <select className="sp-input flex-[1]" value={item.unit} onChange={e => updateItem(idx, 'unit', e.target.value)}>
                      <option>Stk</option><option>m</option><option>m²</option><option>m³</option>
                      <option>kg</option><option>l</option><option>Pkt</option><option>Palette</option>
                    </select>
                    <button type="button" onClick={() => removeItem(idx)}
                      className="sp-btn-icon !w-9 !h-9 text-slate-400 hover:text-red-500 flex-shrink-0">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button type="button" onClick={closeForm} className="sp-btn-secondary">Abbrechen</button>
              <button type="submit" className="sp-btn-primary" disabled={save.isPending}>
                {save.isPending ? 'Speichern...' : editId ? 'Aktualisieren' : 'Anlegen'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="text-center py-12 text-slate-400">Laden...</div>
      ) : notes.length === 0 ? (
        <div className="sp-card p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-50 to-brand-100 flex items-center justify-center mx-auto mb-3">
            <svg className="w-7 h-7 text-brand-500" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /><path d="M9 14l2 2 4-4" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-slate-700 mb-1">Keine Lieferscheine</h3>
          <p className="text-sm text-slate-500">Dokumentiere eingehende Materiallieferungen für deine Projekte.</p>
        </div>
      ) : (
        <div className="sp-card overflow-hidden">
          <table className="sp-table">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Nummer</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Projekt</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Lieferant</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Datum</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Positionen</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Angenommen</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {notes.map(n => (
                <tr key={n.id} className="border-t border-surface-100 hover:bg-surface-50 cursor-pointer" onClick={() => openEdit(n)}>
                  <td className="px-4 py-3 font-medium text-slate-800">{n.number}</td>
                  <td className="px-4 py-3 text-slate-600">{n.project?.title || '–'}</td>
                  <td className="px-4 py-3 text-slate-600">{n.supplier || '–'}</td>
                  <td className="px-4 py-3 text-slate-600">{new Date(n.deliveryDate).toLocaleDateString('de-DE')}</td>
                  <td className="px-4 py-3 text-slate-600">{n.items?.length || 0}</td>
                  <td className="px-4 py-3 text-slate-600">{n.receivedBy || '–'}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={(e) => { e.stopPropagation(); if (confirm('Lieferschein löschen?')) remove.mutate(n.id); }}
                      className="sp-btn-icon !w-8 !h-8 text-slate-400 hover:text-red-500"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
